import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

// Shape of company_overview.registered_address_lat_long_and_zoom from company_profile_data.
export interface CompanyProfileHeroLocation {
  lat_long?: [number, number] | number[] | null;
  zoom_level?: number | null;
  show_point?: boolean | null;
}

// Google returns a 16px generic globe (with a 404) when it has no favicon for a domain.
const GENERIC_FAVICON_SIZE = 16;

// Shared hero card for the skinned company profiles: map banner, logo tile and identity block.
// Skins project their own content into the slots below; colours come from the --profile-* custom properties.
//   [heroBadge]      top of the primary column, before the identity (e.g. a heat score)
//   [heroNamePrefix] inside the heading, before the company name (e.g. a country flag)
//   [heroMeta]       the meta line under the name (status pill, company number, ...)
//   (default)        primary column content after the identity (description, actions)
//   [heroAside]      the right-hand column (e.g. products, accounts calendar)
@Component({
  selector: 'company-profile-hero',
  templateUrl: './company-profile-hero.component.html',
  styleUrls: ['./company-profile-hero.component.css'],
  standalone: false,
})
export class CompanyProfileHeroComponent implements OnChanges {
  @Input() name = '';
  @Input() eyebrow = '';
  @Input() headingId = 'company-profile-hero-name';
  @Input() location?: CompanyProfileHeroLocation | null;
  @Input() mapLabel = 'Registered location';
  @Input() mapUnavailableLabel = 'Location unavailable';
  @Input() markerFill = '#244a3b';
  @Input() logoDomain = '';
  @Input() logoFallbackIconClass = 'fal fa-building';
  // Treat Google's generic globe as "no logo" so the fallback icon shows instead of a blurry placeholder.
  @Input() hideGenericLogo = true;

  logoFailed = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['logoDomain']) this.logoFailed = false;
  }

  get coordinates(): [number, number] | null {
    const latLong = this.location?.lat_long;
    if (!Array.isArray(latLong) || latLong.length < 2) return null;
    const [latitude, longitude] = latLong.map(Number);
    return Number.isFinite(latitude) && Number.isFinite(longitude) ? [latitude, longitude] : null;
  }

  get zoom(): number {
    return Number(this.location?.zoom_level) || 0;
  }

  get showMarker(): boolean {
    return !!this.location?.show_point;
  }

  get logoSrc(): string {
    return this.logoDomain && !this.logoFailed
      ? `https://www.google.com/s2/favicons?sz=128&domain=${encodeURIComponent(this.logoDomain)}`
      : '';
  }

  onLogoLoad(image: HTMLImageElement): void {
    if (this.hideGenericLogo && image.naturalWidth > 0 && image.naturalWidth <= GENERIC_FAVICON_SIZE) {
      this.logoFailed = true;
    }
  }
}
